/* eslint-disable no-console */
import {Table, Typography, Flex} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import React, {useEffect} from 'react';

import {ReactComponent as AICollapseIcon} from '../../assets/icons/AICollapseIcon.svg';
import {ReactComponent as CACollapseIcon} from '../../assets/icons/CACollapseIcon.svg';
import {ReactComponent as ESGCollapseIcon} from '../../assets/icons/ESGCollapseIcon.svg';
import {ReactComponent as FeatureNo} from '../../assets/icons/FeatureNo.svg';
import {ReactComponent as FeatureYes} from '../../assets/icons/FeatureYes.svg';
import {ReactComponent as NZStrategyCollapseIcon} from '../../assets/icons/NZStrategyCollapseIcon.svg';
import {ReactComponent as SupportCollapseIcon} from '../../assets/icons/SupportCollapseIcon.svg';

const {Text} = Typography;

interface FeatureRow {
  key: string;
  label: string;
  readOnly: any;
  freeTrial: any;
  free: any;
  pro: any;
  plus: any;
  enterprise: any;
}

interface Section {
  value: string;
  label: string;
  data: FeatureRow[];
}

const ComparePlans: React.FC<{
  comparisonData: Section[];
  freePlanType: string;
}> = ({comparisonData, freePlanType}) => {
  const [expandedRowKeys, setExpandedRowKeys] = React.useState<string[]>([]);

  const renderValue = (val: any) => {
    if (val === true)
      return (
        <Flex justify="center">
          <div className="featureIconContainer greenBackground">
            <FeatureYes />
          </div>
        </Flex>
      );
    if (val === false)
      return (
        <Flex justify="center">
          <div className="featureIconContainer greyBackground">
            <FeatureNo />
          </div>
        </Flex>
      );

    if (val?.includes('<br/>')) {
      const values = val.split('<br/>');

      return (
        <>
          {values.map((item: any, index: number) => (
            <React.Fragment key={index}>
              {index !== 0 && <br />}
              <div style={index === 1 ? {paddingTop: '2px'} : undefined}>
                <Text style={{textAlign: 'center', display: 'block'}}>
                  {item || '-'}
                </Text>
              </div>
            </React.Fragment>
          ))}
        </>
      );
    }

    return (
      <Text style={{textAlign: 'center', display: 'block'}}>{val || '-'}</Text>
    );
  };

  const renderGroupIcon = (key: string) => {
    const map: Record<string, React.ReactNode> = {
      carbonAccounting: <CACollapseIcon />,
      esgReporting: <ESGCollapseIcon />,
      netZeroStrategy: <NZStrategyCollapseIcon />,
      ai: <AICollapseIcon />,
      support: <SupportCollapseIcon />
    };

    return map[key] ?? null;
  };

  const dataSource =
    comparisonData?.map((section) => {
      const groupKey = `group-${section.value}`;

      return {
        key: groupKey,
        isGroup: true,
        label: section.label,
        groupKey: section.value,
        children: section.data.map((f, i) => ({
          key: `${groupKey}-f-${i}`,
          label: f.label,
          readOnly: f.readOnly,
          freeTrial: f.freeTrial,
          free: f.free,
          pro: f.pro,
          plus: f.plus,
          enterprise: f.enterprise
        }))
      };
    }) || [];

  // Auto-expand all groups by default
  useEffect(() => {
    setExpandedRowKeys(dataSource.map((d) => d.key));
  }, [comparisonData]);

  const columns: ColumnsType<any> = [
    {
      title: 'Features',
      key: 'features',
      className: 'align-left',
      render: (_, record) => {
        if (record.isGroup) {
          return (
            <Flex
              justify="space-between"
              align="center"
              style={{width: '100%'}}
            >
              <Flex align="center" gap={12}>
                {renderGroupIcon(record.groupKey)}
                <Text strong style={{fontSize: 16}}>
                  {record.label}
                </Text>
              </Flex>

              {/* Expand/Collapse Icon on the RIGHT */}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  const newKeys = expandedRowKeys.includes(record.key)
                    ? expandedRowKeys.filter((k) => k !== record.key)
                    : [...expandedRowKeys, record.key];
                  setExpandedRowKeys(newKeys);
                }}
                style={{
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',

                  transition: 'all 0.3s'
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  style={{
                    transform: expandedRowKeys.includes(record.key)
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                    transition: 'transform 0.3s',
                    color: '#666'
                  }}
                >
                  <path
                    d="M6 9l6 6 6-6"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </Flex>
          );
        }

        // Child rows (features)
        return <div style={{paddingLeft: 20}}>{record.label}</div>;
      },
      width: 420,
      fixed: 'left'
    },
    {
      title: `${freePlanType === 'free' ? 'Free' : 'Free Trial'}`,
      dataIndex: `${freePlanType}`,
      key: `${freePlanType}`,
      width: 160,
      align: 'center',
      className: 'comparePlanTableHead',
      render: (_, r) => (r.isGroup ? null : renderValue(r[freePlanType]))
    },
    {
      title: 'Pro',
      dataIndex: 'pro',
      key: 'pro',
      width: 160,
      align: 'center',
      className: 'comparePlanTableHead',
      render: (_, r) => (r.isGroup ? null : renderValue(r.pro))
    },
    {
      title: 'Plus',
      dataIndex: 'plus',
      key: 'plus',
      width: 160,
      align: 'center',
      className: 'comparePlanTableHead',
      render: (_, r) => (r.isGroup ? null : renderValue(r.plus))
    },
    {
      title: 'Enterprise',
      dataIndex: 'enterprise',
      key: 'enterprise',
      width: 200,
      align: 'center',
      className: 'comparePlanTableHead',
      render: (_, r) => (r.isGroup ? null : renderValue(r.enterprise))
    }
  ];

  return (
    <>
      <div className="letsCompareText">Let’s Compare the Plans!</div>
      <div>
        <Table
          className="compare-plans-table ant-table-wrapper-pricing-table"
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          bordered
          size="large"
          rowKey="key"
          scroll={{x: 1000}}
          sticky={{offsetHeader: 80}}
          expandable={{
            expandedRowKeys,
            onExpand: (expanded, record) => {
              setExpandedRowKeys((prev) =>
                expanded
                  ? [...prev, record.key]
                  : prev.filter((k) => k !== record.key)
              );
            },
            // We hide default icon and use custom one inside the cell
            expandIcon: () => null
          }}
          rowClassName={(record) =>
            record.isGroup ? 'group-header-row' : 'feature-row'
          }
        />
      </div>
    </>
  );
};

export default ComparePlans;
